'use client';

import type { EditedPlanPrompt, PlanPromptItem, PlanSummaryPayload } from '@/@studio-ai/types';
import { humanizeLabel } from '@/@studio-ai/utils/text-utils';
import { Button, Chip, Divider, Textarea } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useEffect, useMemo, useState } from 'react';

/** Build a stable key for a prompt row (order is canonical, type is fallback). */
function promptKey(p: PlanPromptItem, index: number): string {
    return `${p.order}-${p.type || 'prompt'}-${index}`;
}

export function PlanSummaryCard({
    payload,
    onCreateReport,
}: {
    payload: PlanSummaryPayload;
    onCreateReport?: (runId: string, editedPrompts?: EditedPlanPrompt[]) => void;
}) {
    const { runId, templateName, brand, gatheredInputs, modules, prompts } = payload;

    const parameterCount = Object.keys(gatheredInputs).length;
    const moduleCount = modules?.length ?? 0;
    const promptList = useMemo<PlanPromptItem[]>(() => prompts ?? [], [prompts]);
    const [showPrompts, setShowPrompts] = useState(false);
    const [promptDrafts, setPromptDrafts] = useState<Record<string, string>>(() =>
        Object.fromEntries(promptList.map((p, i) => [promptKey(p, i), p.text])),
    );

    useEffect(() => {
        setPromptDrafts((prev) => {
            const next: Record<string, string> = {};
            promptList.forEach((p, i) => {
                const key = promptKey(p, i);
                next[key] = prev[key] ?? p.text;
            });
            return next;
        });
    }, [promptList]);

    const dirtyPromptKeys = useMemo(() => {
        const out = new Set<string>();
        promptList.forEach((p, i) => {
            const key = promptKey(p, i);
            const draft = promptDrafts[key];
            if (typeof draft === 'string' && draft.trim() !== p.text.trim()) {
                out.add(key);
            }
        });
        return out;
    }, [promptList, promptDrafts]);

    const handleCreateReport = () => {
        if (!onCreateReport) return;
        const edits: EditedPlanPrompt[] = [];
        promptList.forEach((p, i) => {
            const key = promptKey(p, i);
            if (!dirtyPromptKeys.has(key)) return;
            const text = (promptDrafts[key] ?? '').trim();
            if (!text) return;
            edits.push({
                order: p.order,
                type: p.type,
                section_name: p.section_name,
                text,
            });
        });
        onCreateReport(runId, edits.length > 0 ? edits : undefined);
    };

    const resetPrompt = (key: string, original: string) => {
        setPromptDrafts((prev) => ({ ...prev, [key]: original }));
    };

    const hasPrompts = promptList.length > 0;

    return (
        <div className="w-full rounded-xl border border-default-200/60 bg-content1 p-5 shadow-sm">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                    <h3 className="text-medium font-semibold text-foreground">
                        {templateName}
                        {brand && <span className="text-default-400"> · {brand}</span>}
                    </h3>
                    <span className="text-tiny text-default-400">
                        {parameterCount} parameter{parameterCount !== 1 ? 's' : ''}
                        {moduleCount > 0 &&
                            ` · ${moduleCount} module${moduleCount !== 1 ? 's' : ''}`}
                    </span>
                </div>
            </div>

            <Divider className="my-4" />

            {/* Parameters grid */}
            <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                {Object.entries(gatheredInputs).map(([key, value]) => (
                    <div key={key} className="flex flex-col gap-1">
                        <span className="text-tiny font-medium uppercase tracking-wide text-default-400">
                            {humanizeLabel(key)}
                        </span>
                        <span className="text-small text-foreground">{humanizeLabel(value)}</span>
                    </div>
                ))}
            </div>

            {/* Modules row — shows every plan item the agent selected
                (both standard catalog modules and custom/ad-hoc ones). */}
            {moduleCount > 0 && (
                <>
                    <Divider className="my-4" />
                    <div className="flex flex-col gap-1">
                        <span className="text-tiny font-medium uppercase tracking-wide text-default-400">
                            Plan items
                        </span>
                        <span className="text-small text-foreground">
                            {modules!.map((m) => humanizeLabel(m.name)).join(', ')}
                        </span>
                    </div>
                </>
            )}

            {/* Editable prompts drawer — only visible after the user clicks
                "Edit plan". Shown inline on the card; edits are carried
                forward when the user clicks "Build a Report". */}
            {hasPrompts && showPrompts && (
                <>
                    <Divider className="my-4" />
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-small font-medium uppercase tracking-wide text-default-400">
                                Plan prompts
                            </span>
                            <span className="text-small text-default-400">
                                {promptList.length} prompt
                                {promptList.length !== 1 ? 's' : ''} will be sent when you build the
                                report
                                {dirtyPromptKeys.size > 0 && ` · ${dirtyPromptKeys.size} edited`}
                            </span>
                        </div>
                        <div className="flex flex-col gap-3">
                            {promptList.map((p, index) => {
                                const key = promptKey(p, index);
                                const draft = promptDrafts[key] ?? p.text;
                                const isDirty = dirtyPromptKeys.has(key);
                                const title =
                                    (p.section_name && p.section_name.trim()) ||
                                    humanizeLabel(p.type) ||
                                    `Prompt ${index + 1}`;
                                return (
                                    <div
                                        key={key}
                                        className="flex flex-col gap-1.5 rounded-lg border border-default-200/60 bg-content2/40 px-3 py-2.5"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-small font-semibold text-foreground">
                                                    {humanizeLabel(title)}
                                                </span>
                                                {p.type && (
                                                    <Chip
                                                        size="sm"
                                                        variant="flat"
                                                        color="default"
                                                        classNames={{
                                                            base: 'h-5',
                                                            content:
                                                                'text-small font-medium px-1.5',
                                                        }}
                                                    >
                                                        {humanizeLabel(p.type)}
                                                    </Chip>
                                                )}
                                            </div>
                                            {isDirty && (
                                                <button
                                                    type="button"
                                                    onClick={() => resetPrompt(key, p.text)}
                                                    className="flex items-center gap-1 text-small text-default-500 transition-colors hover:text-default-700"
                                                >
                                                    <Icon
                                                        icon="lucide:rotate-ccw"
                                                        width={12}
                                                        height={12}
                                                    />
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                        <Textarea
                                            minRows={3}
                                            maxRows={12}
                                            value={draft}
                                            onValueChange={(value) =>
                                                setPromptDrafts((prev) => ({
                                                    ...prev,
                                                    [key]: value,
                                                }))
                                            }
                                            variant="bordered"
                                            classNames={{
                                                input: 'text-mini leading-relaxed font-mono',
                                                inputWrapper: isDirty
                                                    ? 'border-warning-300 data-[hover=true]:border-warning-400'
                                                    : undefined,
                                            }}
                                            aria-label={`Plan prompt for ${title}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {/* Actions */}
            <div className="mt-5 flex items-center gap-3">
                {hasPrompts && (
                    <Button
                        size="sm"
                        variant="bordered"
                        className="font-medium"
                        onPress={() => setShowPrompts((prev) => !prev)}
                        radius="full"
                        startContent={
                            <Icon
                                icon={showPrompts ? 'lucide:chevron-up' : 'lucide:pencil'}
                                width={14}
                                height={14}
                            />
                        }
                    >
                        {showPrompts ? 'Hide plan' : 'Edit plan'}
                        {dirtyPromptKeys.size > 0
                            ? ` (${dirtyPromptKeys.size} edit${dirtyPromptKeys.size !== 1 ? 's' : ''})`
                            : ''}
                    </Button>
                )}
                {onCreateReport && (
                    <Button
                        size="sm"
                        className="bg-mfour-green font-medium text-white"
                        endContent={<Icon icon="lucide:arrow-right" width={14} height={14} />}
                        onPress={handleCreateReport}
                        radius="full"
                    >
                        Build a Report from this
                    </Button>
                )}
            </div>
            <p className="mt-4 flex justify-center text-mini text-default-500">
                Want to make changes? Feel free to type below about what you want to change.
            </p>
        </div>
    );
}
